import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { PosHubService } from 'src/app/services/pos-hub.service';
import { ActivatedRoute } from '@angular/router';
import { NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'src/app/services/alert.service';
import { PosDeviceService, PosTransactionService } from 'src/app/services/pos-device.service';
import { PosDevice, PosTransaction, PosTransactionFilter, PosTransactionSummary } from 'src/app/models/pos/pos-device';
import { KpiExtra } from 'src/app/shared/components/kpi-card/kpi-card.component';
import {
  PosDocumentStatus,
  PosDocumentStatusLabels,
  PosLineModeLabels,
  PosTransactionStatus,
  PosTransactionStatusLabels,
  PosTransactionType,
  posTransactionStatusColor,
  posTransactionStatusLabel,
} from 'src/app/models/enums/pos-enums';
import { extractErrorMessage, toIsoDate } from '../../accounting/shared/e-invoice.helpers';
import { PosVoidModalComponent } from './pos-void-modal/pos-void-modal.component';
import { PosRefundModalComponent } from './pos-refund-modal/pos-refund-modal.component';
import { PosDocuments, extractPosDocuments } from './pos-json.helpers';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { SortablePagingRequest } from 'src/app/core/models/requests/base-paging-request';
import { DEFAULT_LIST_SORT, ListSort } from '../../accounting/shared/sort-header/sort-header.component';
import { BasePagingResponse } from 'src/app/core/models/responses/base-paging-response';

/**
 * POS cihaz işlemleri ve mutabakat (pos-entegrasyonu.md K7, K9).
 *
 * Sonuç sağlayıcıdan okunur (geri çağrı yalnızca tetikleyici); arka plan servisi açık
 * işlemleri düzenli yoklar. Bu ekran özellikle "onaylı ama satışa bağlanmamış"
 * işlemleri gösterir: kartından para çekilmiş ama satış kaydı yazılmamış müşteri.
 *
 * `?id=` ile açılırsa (ör. satış detayındaki POS tahsilatı bağlantısı) o işlemin ayrıntısı açılır.
 *
 * Canlı: PosHub'dan değişen işlem gelince (geri çağrı, mutabakat, başka kasiyer) bulunulan sayfa ve
 * KPI'lar sessizce yenilenir, değişen satır kısa süre vurgulanır, açık ayrıntı da tazelenir (K23).
 */
@Component({
  selector: 'app-pos-transactions',
  templateUrl: './pos-transactions.component.html',
  // Canlı bildirimle değişen satır kısa süre vurgulanır.
  styles: [`
    .pos-row-flash > td { animation: pos-row-flash 2.5s ease-out; }
    @keyframes pos-row-flash {
      0%, 30% { background-color: rgba(var(--bs-primary-rgb), .18); }
      100% { background-color: transparent; }
    }
  `],
})
export class PosTransactionsComponent extends BaseListComponent implements OnInit, AfterViewInit, OnDestroy {
  breadCrumbItems = [{ label: 'Kasa' }, { label: 'POS İşlemleri', active: true }];

  readonly statusLabels = PosTransactionStatusLabels;
  readonly documentLabels = PosDocumentStatusLabels;
  readonly lineModeLabels = PosLineModeLabels;
  readonly statusColor = posTransactionStatusColor;
  /** İptal işleminde "Onaylandı" yerine "İptal edildi" gibi türe göre etiket. */
  readonly statusLabel = posTransactionStatusLabel;
  readonly Status = PosTransactionStatus;
  readonly DocumentStatus = PosDocumentStatus;
  readonly Type = PosTransactionType;
  readonly statuses = [
    PosTransactionStatus.Pending, PosTransactionStatus.InProgress, PosTransactionStatus.Suspended,
    PosTransactionStatus.Approved, PosTransactionStatus.Declined, PosTransactionStatus.Cancelled,
  ];

  request: SortablePagingRequest<PosTransactionFilter> = {
    pageIndex: this.pageIndex,
    itemPerPage: this.itemPerPage,
    filter: this.defaultFilter(),
    sortBy: null,
    sortDescending: false,
  };
  /**
   * Tarih aralığı süzgecinin modeli; süzgeç `yyyy-MM-dd` metni tutuyor. Getter değil alan:
   * her döngüde yeni nesne dönseydi datepicker kutuyu her algılamada yeniden yazardı.
   */
  dateStart: NgbDateStruct | null = this.toDateStruct(this.request.filter!.from);
  dateEnd: NgbDateStruct | null = this.toDateStruct(this.request.filter!.to);
  /** Başlıktan sıralama; sıralama sunucuda (liste sayfalı). Boşsa en yeni önce. */
  sort: ListSort = { ...DEFAULT_LIST_SORT };
  response: BasePagingResponse<PosTransaction> | null = null;

  transactions: PosTransaction[] = [];
  devices: PosDevice[] = [];
  busyId: number | null = null;
  /** KPI'lar — dönem ve cihaz süzgecine göre; durum süzgeci uygulanmaz. */
  summary: PosTransactionSummary | null = null;
  summaryLoading = false;
  /**
   * KPI kartlarının ek satırları ve dönem etiketi. Özet gelince bir kez kurulur: getter olsaydı
   * her değişiklik algılamasında yeni dizi dönerdi — kartların *ngFor'u her döngüde DOM'u
   * yeniden kurar, geliştirme modunda her döngü ExpressionChanged hatası atar ve sayfa kilitlenirdi.
   */
  netExtras: KpiExtra[] = [];
  saleExtras: KpiExtra[] = [];
  voidExtras: KpiExtra[] = [];
  attentionExtras: KpiExtra[] = [];
  periodLabel = '';

  detail: PosTransaction | null = null;
  detailLoading = false;
  /** Ayrıntıdaki işlemin cihazın düzenlediği mali belgeleri ve görüntüleme bağlantısı. */
  detailDocuments: PosDocuments = { documents: [], viewUrl: null };
  /** Sağlayıcıya giden istek ve gelen cevap teknik ayrıntıdır; modal açılırken kapalı gelir. */
  requestOpen = false;
  responseOpen = false;
  @ViewChild('detailModal') detailModalTemplate!: TemplateRef<any>;

  /** Canlı bildirimle değişen, kısa süre vurgulanan satırlar. */
  flashIds = new Set<number>();
  /** Art arda gelen bildirimler tek yenilemede toplanır. */
  private liveIds = new Set<number>();
  private liveSubscription?: Subscription;
  private flashTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private service: PosTransactionService,
    private deviceService: PosDeviceService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private posHub: PosHubService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.deviceService.list(false).subscribe({ next: res => this.devices = res.data || [], error: () => this.devices = [] });
    this.reloadData(true);
    this.liveSubscription = this.posHub.changed$.pipe(
      tap(change => this.liveIds.add(change.id)),
      debounceTime(400),
    ).subscribe(() => this.applyLiveChanges());
  }

  ngOnDestroy(): void {
    this.liveSubscription?.unsubscribe();
    if (this.flashTimer) clearTimeout(this.flashTimer);
  }

  /**
   * Bulunulan sayfa ve KPI'lar yükleme göstergesi olmadan yenilenir: yeni işlem süzgece uyuyorsa
   * listeye girer, iptal onaylanınca asıl çekimin "karta iade edildi" durumu da güncellenir.
   */
  private applyLiveChanges(): void {
    const ids = [...this.liveIds];
    this.liveIds.clear();
    this.reloadData(false, true, ids);
    this.loadSummary(true);
    if (this.detail && ids.includes(this.detail.id)) this.refreshDetailSilently(this.detail.id);
  }

  ngAfterViewInit(): void {
    const id = Number(this.route.snapshot.queryParamMap.get('id'));
    // Ayrıntı şablonu görünüm kurulunca hazır; aynı döngüde açmak değişiklik algılama hatası verir.
    if (id > 0) setTimeout(() => this.openDetailById(id));
  }

  /**
   * Süzgeç değişince ilk sayfaya döner ve KPI'ları yeniler; sayfa değişince yalnızca o sayfayı getirir.
   * `silent`: canlı bildirimde yükleme göstergesi ve hata uyarısı yok; `flash` satırları vurgulanır.
   */
  reloadData(isFiltered: boolean = false, silent = false, flash: number[] = []): void {
    if (isFiltered) {
      this.request.pageIndex = 1;
      this.loadSummary();
    }
    localStorage.setItem('itemPerPage', this.request.itemPerPage.toString());
    if (!silent) this.Loading = true;

    const filter = this.request.filter!;
    const request: SortablePagingRequest<PosTransactionFilter> = {
      ...this.request,
      // "Bitiş" günü dahil: sunucu tarih-saat karşılaştırıyor.
      filter: this.apiFilter(filter),
    };
    this.service.listPaged(request).subscribe({
      next: res => {
        this.response = res;
        this.transactions = res?.data?.Items || [];
        this.Loading = false;
        if (flash.length) this.flash(flash);
      },
      error: error => {
        this.Loading = false;
        // Canlı yenileme başarısızsa eldeki liste kalır; kullanıcı bir şey yapmadı, uyarı gösterilmez.
        if (silent) return;
        this.transactions = [];
        this.alertService.showErrorsToast([extractErrorMessage(error, 'POS işlemleri alınamadı.')]);
      },
    });
  }

  private flash(ids: number[]): void {
    const visible = ids.filter(id => this.transactions.some(t => t.id === id));
    if (!visible.length) return;
    this.flashIds = new Set(visible);
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => this.flashIds = new Set<number>(), 2500);
  }

  /** Açık ayrıntı penceresi göstergesiz tazelenir; teknik bölümlerin açık/kapalı hali korunur. */
  private refreshDetailSilently(id: number): void {
    this.service.get(id).subscribe({
      next: res => {
        if (this.detail?.id !== id || !res.data) return;
        this.detail = res.data;
        this.detailDocuments = extractPosDocuments(this.detail.responseJson);
      },
      error: () => {},
    });
  }

  /** Başlıktan sıralama. Sıra değişince ilk sayfaya dönülür; KPI'lar sıradan etkilenmez. */
  onSort(sort: ListSort): void {
    this.sort = sort;
    this.request.sortBy = sort.sortBy;
    this.request.sortDescending = sort.sortDescending;
    this.request.pageIndex = 1;
    this.reloadData();
  }

  /** İşlemden sonra bulunulan sayfa ve KPI'lar yenilenir. */
  load(): void {
    this.reloadData();
    this.loadSummary();
  }

  private loadSummary(silent = false): void {
    if (!silent) this.summaryLoading = true;
    this.service.summary(this.apiFilter(this.request.filter!)).subscribe({
      next: res => {
        this.setSummary(res?.data ?? null);
        this.summaryLoading = false;
      },
      error: () => {
        // Canlı yenilemede eldeki KPI'lar kalır.
        if (!silent) this.setSummary(null);
        this.summaryLoading = false;
      },
    });
  }

  /** "Bitiş" günü dahil: sunucu tarih-saat karşılaştırıyor. */
  private apiFilter(filter: PosTransactionFilter): PosTransactionFilter {
    return { ...filter, to: filter.to ? filter.to + 'T23:59:59' : null };
  }

  /** Tarih süzgecinden bağımsız: tüm onaylı ama satışa bağlanmamış çekimler. */
  get unlinkedCount(): number {
    return this.summary?.unlinkedCount || 0;
  }

  // ── KPI ────────────────────────────────────────────────────────────────

  private static readonly moneyFormat = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  money(value?: number | null): string {
    return `${PosTransactionsComponent.moneyFormat.format(value || 0)} ₺`;
  }

  private setSummary(summary: PosTransactionSummary | null): void {
    this.summary = summary;

    const { from, to } = this.request.filter!;
    const format = (iso?: string | null) => iso ? iso.split('-').reverse().join('.') : '…';
    this.periodLabel = `${format(from)} – ${format(to)}`;

    if (!summary) {
      this.netExtras = this.saleExtras = this.voidExtras = this.attentionExtras = [];
      return;
    }

    this.netExtras = [
      { label: 'Çekim', value: this.money(summary.approvedSaleAmount) },
      { label: 'İptal ve iade', value: '−' + this.money(summary.voidAmount + (summary.refundAmount || 0)) },
    ];

    this.saleExtras = [{ label: 'Adet', value: String(summary.approvedSaleCount) }];
    if (summary.installmentSaleCount) this.saleExtras.push({ label: 'Taksitli', value: String(summary.installmentSaleCount) });
    if (summary.customerCommissionAmount) {
      this.saleExtras.push({ label: 'Yansıtılan komisyon', value: PosTransactionsComponent.moneyFormat.format(summary.customerCommissionAmount) });
    }

    this.voidExtras = [
      { label: 'İptal', value: `${summary.voidCount} · ${this.money(summary.voidAmount)}` },
      { label: 'Satır iadesi', value: `${summary.refundCount || 0} · ${this.money(summary.refundAmount)}` },
    ];

    this.attentionExtras = [
      { label: 'Bağlanmamış tutar', value: this.money(summary.unlinkedAmount) },
      { label: 'Sonuç bekleyen', value: String(summary.pendingCount) },
      { label: 'Reddedilen', value: String(summary.declinedCount) },
    ];
  }

  clearFilter(): void {
    this.request.filter = this.defaultFilter();
    this.dateStart = this.toDateStruct(this.request.filter.from);
    this.dateEnd = this.toDateStruct(this.request.filter.to);
    this.reloadData(true);
  }

  onDateRangeChange(): void {
    this.request.filter!.from = toIsoDate(this.dateStart);
    this.request.filter!.to = toIsoDate(this.dateEnd);
    this.reloadData(true);
  }

  /** Varsayılandan (son 7 gün, tümü) farklı süzgeç sayısı. */
  get activeFilterCount(): number {
    const filter = this.request.filter!;
    const defaults = this.defaultFilter();
    let count = 0;
    if (filter.from !== defaults.from || filter.to !== defaults.to) count++;
    if (filter.status != null) count++;
    if (filter.posDeviceId != null) count++;
    if (filter.onlyUnlinked) count++;
    return count;
  }

  /** "Filtreler" menüsündeki süzgeçler; cihaz seçimi dışarıda göründüğü için sayılmaz. */
  get panelFilterCount(): number {
    return this.activeFilterCount - (this.request.filter!.posDeviceId != null ? 1 : 0);
  }

  private defaultFilter(): PosTransactionFilter {
    return { from: this.isoDate(-7), to: this.isoDate(0), status: null, posDeviceId: null, onlyUnlinked: false };
  }

  trackById(_: number, t: PosTransaction): number {
    return t.id;
  }

  /** Kartından para çekildi ama satış kaydı yazılmadı. Cihazdan iptal edilip karta iade edildiyse sayılmaz. */
  isUnlinked(t: PosTransaction): boolean {
    return t.type === PosTransactionType.Sale && t.status === PosTransactionStatus.Approved && !t.salesGroupId && !t.salesId
      && !this.isVoided(t);
  }

  /** Satış cihazdan iptal edildi ve tutar karta iade edildi (iptal kaydı başka sayfada olabilir; sunucudan gelir). */
  isVoided(t: PosTransaction): boolean {
    return t.voidStatus === PosTransactionStatus.Approved;
  }

  canRefresh(t: PosTransaction): boolean {
    return !t.isFinal || t.documentStatus === PosDocumentStatus.Pending || t.documentStatus === PosDocumentStatus.Unknown;
  }

  canCancel(t: PosTransaction): boolean {
    return !t.isFinal && !t.cancelRequestedAt;
  }

  /** Onaylı satış cihazdan iptal edilebilir; satışa bağlıysa satış kaydı ayrıca iptal edilmeli. */
  canVoid(t: PosTransaction): boolean {
    return t.type === PosTransactionType.Sale && t.status === PosTransactionStatus.Approved
      // Sunucuyla aynı kural: süren ya da tamamlanmış iptal varsa yeni iptal açılmaz;
      // reddedilen ya da geri çekilen iptal yeniden denenebilir (voidStatus boş gelir).
      && t.voidStatus == null
      // İptal belgenin tamamını iade eder; satır iadesi yapılmış satışta kalan satırlar iadeyle döner.
      && !t.refundedLineNos?.length;
  }

  /**
   * Onaylı, iptal edilmemiş satıştan satır iadesi (K24). Sürmekte olan iade bitmeden yeni iade
   * açılmaz; iade edilmemiş satır kalıp kalmadığı pencerede görülür (listede satırlar gelmez).
   */
  canRefund(t: PosTransaction): boolean {
    return t.type === PosTransactionType.Sale && t.status === PosTransactionStatus.Approved
      && t.voidStatus == null && !t.hasOpenRefund;
  }

  /** İade cihaza gider ve pencere cihazın sonucunu bekler; istek gönderildiyse liste yenilenir. */
  refund(t: PosTransaction): void {
    const modalRef = this.modalService.open(PosRefundModalComponent, { backdrop: 'static', keyboard: false, size: 'lg' });
    modalRef.componentInstance.sale = t;
    modalRef.result.then(() => this.load(), () => {});
  }

  /** Detaydaki satır daha önce iade edildi mi (yalnızca satış çekiminde dolu). */
  isLineRefunded(lineNo: number): boolean {
    return !!this.detail?.refundedLineNos?.includes(lineNo);
  }

  refresh(t: PosTransaction): void {
    this.run(t, this.service.refresh(t.id), 'Durum güncellendi.');
  }

  cancel(t: PosTransaction): void {
    this.alertService
      .showConfirmAlert('Cihazda bekleyen istek geri çekilecek. Kart çekildiyse iptal gerçekleşmez; durum yoklanmaya devam eder.', 'Geri Çek', 'Vazgeç')
      .then(result => {
        if (result.isConfirmed) this.run(t, this.service.cancel(t.id), 'İptal isteği gönderildi.');
      });
  }

  /** İptal cihaza gider ve pencere cihazın sonucunu bekler; istek gönderildiyse liste yenilenir. */
  void(t: PosTransaction): void {
    const modalRef = this.modalService.open(PosVoidModalComponent, { backdrop: 'static', keyboard: false });
    modalRef.componentInstance.sale = t;
    modalRef.result.then(() => this.load(), () => {});
  }

  private run(t: PosTransaction, request: ReturnType<PosTransactionService['refresh']>, success: string): void {
    this.busyId = t.id;
    request.subscribe({
      next: () => {
        this.busyId = null;
        this.alertService.showSuccess(success);
        this.load();
      },
      error: error => {
        this.busyId = null;
        this.alertService.showErrorsToast([extractErrorMessage(error, 'İşlem yapılamadı.')]);
      },
    });
  }

  openDetail(t: PosTransaction): void {
    this.openDetailById(t.id);
  }

  openDetailById(id: number): void {
    this.modalService.open(this.detailModalTemplate, { size: 'xl', scrollable: true });
    this.loadDetail(id);
  }

  /** Açık ayrıntı penceresinde başka bir işleme geçer (ör. iptalden asıl çekime). */
  loadDetail(id: number): void {
    this.detail = null;
    this.detailDocuments = { documents: [], viewUrl: null };
    this.detailLoading = true;
    this.requestOpen = false;
    this.responseOpen = false;
    this.service.get(id).subscribe({
      next: res => {
        this.detail = res.data ?? null;
        this.detailDocuments = extractPosDocuments(this.detail?.responseJson);
        this.detailLoading = false;
      },
      error: error => {
        this.detailLoading = false;
        this.alertService.showErrorsToast([extractErrorMessage(error, 'İşlem ayrıntısı alınamadı.')]);
      },
    });
  }

  /** `yyyy-MM-dd` parçalanarak okunur; `new Date(iso)` UTC sayar ve batı saat dilimlerinde bir gün kayar. */
  private toDateStruct(iso?: string | null): NgbDateStruct | null {
    if (!iso) return null;
    const [year, month, day] = iso.split('-').map(Number);
    return year && month && day ? { year, month, day } : null;
  }

  private isoDate(offsetDays: number): string {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}
